import type { TSavedSlide } from "@/shared/types";
import { useGetSlideContentQuery } from "@api/slides-library";
import Checkbox from "@components/ui/checkbox";
import type { StaticEntityType } from "@config";
import useAddSlides from "@editor/hooks/use-add-slides";
import { useEnsureReplaceNewSlide } from "@editor/hooks/use-ensure-replace-new-slide";
import { cn } from "@lib/cn";
import {
  getRecentlyUsedCategory,
  SlidePreview,
  useRecentlyUsedStore,
  useSlidesLibraryStore,
  useSlidesLibraryStoreShallow,
} from "@modules/slides-library/shared";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { memo, useCallback, useEffect, useState } from "react";
import { useIntersectionObserver } from "usehooks-ts";
import { SelectionOrderBadge } from "@modules/slides-library/shared/components/selection-order-badge";
import { SingleSlideActions } from "./single-slide-actions";
import { SavedSlideActions } from "./saved-slide-actions";

export const SingleSlide = memo(function SingleSlide({
  slide,
}: {
  slide: TSavedSlide;
}) {
  // React Compiler opt-out: the Zustand `isSelected` selector returns a new
  // callback-bound value on every store change, which defeats memoization and
  // trips the compiler into creating a fresh component identity per render.
  "use no memo";
  const dispatch = useAppDispatch();
  const { handleAddSlides } = useAddSlides();
  const ensureReplaceNewSlide = useEnsureReplaceNewSlide();
  const { ref, isIntersecting } = useIntersectionObserver({
    threshold: 0.3,
  });
  const { view, isInMultiSelectMode, handleMultiselectSlide, isSelected } =
    useSlidesLibraryStoreShallow((s) => ({
      view: s.view,
      isInMultiSelectMode: s.isInMultiSelectMode(isIntersecting),
      handleMultiselectSlide: s.handleMultiselectSlide,
      isSelected: s.isSelected(slide._id),
    }));
  const [shouldRender, setShouldRender] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { data: slideJSON } = useGetSlideContentQuery(
    { id: slide._id },
    {
      skip: !isIntersecting,
    },
  );

  useEffect(() => {
    if (isIntersecting && slideJSON) setShouldRender(true);
    else {
      const timeout = setTimeout(() => {
        setShouldRender(false);
      }, 200);
      return () => clearTimeout(timeout);
    }
  }, [isIntersecting, slideJSON]);

  const handleClick = useCallback(() => {
    if (!slideJSON) return;
    // Clicks that belong to the hover buttons never reach here -- the overlay
    // stops them, including the ones the portalled AI prompt produces. See
    // saved-slide-actions.tsx. (The Layouts card avoids the whole question by
    // never adding on a card click at all; adding is the button's job there.)
    if (isInMultiSelectMode || view === "library") {
      handleMultiselectSlide(
        {
          id: slide._id,
          slide: slideJSON as StaticEntityType,
          type: "saved",
          savedSlide: slide,
        },
        isSelected ? "remove" : "add",
      );
      return;
    }
    const { type: libraryType, templateTab } = useSlidesLibraryStore.getState();
    const category = getRecentlyUsedCategory(libraryType, templateTab);
    if (category === "user-slides" || category === "team-slides") {
      useRecentlyUsedStore.getState().recordSavedSlide(category, slide);
    }
    // A saved slide carries its own content. If a blank slide was just added
    // (SuggestedLayouts flow), replace it in place instead of inserting a new
    // slide — assert the replace intent explicitly since the lingering
    // ai-slides placement is unreliable (the dialog resets it on unmount).
    // preserveContentText:false then keeps the saved slide's own text intact
    // rather than overwriting it with the blank slide's placeholders.
    ensureReplaceNewSlide();
    handleAddSlides([slideJSON as StaticEntityType], {
      preserveContentText: false,
    });
    dispatch(closeDialog("slides-library"));
    dispatch(closeDialog("saved-slides-dialog"));
    dispatch(closeDialog("layouts-dialog"));
    dispatch(closeDialog("templates-dialog"));
  }, [
    isInMultiSelectMode,
    view,
    slide,
    slideJSON,
    isSelected,
    handleMultiselectSlide,
    handleAddSlides,
    ensureReplaceNewSlide,
    dispatch,
  ]);

  return (
    <div
      data-menu-open={isMenuOpen || isSelected}
      className={cn(
        "tw-group/slide tw-relative tw-flex tw-h-[170px] tw-w-full tw-min-w-0 tw-flex-col tw-gap-2 tw-rounded-xl tw-bg-white tw-p-2 tw-transition-colors hover:tw-bg-blue-50",
        (isMenuOpen || isSelected) && "tw-bg-blue-50",
      )}
    >
      <div
        className="tw-relative tw-cursor-pointer"
        onClick={handleClick}
        ref={ref}
      >
        <SlidePreview
          slide={slideJSON as StaticEntityType}
          isIntersecting={isIntersecting}
          shouldRender={shouldRender}
        />
        {/*
          The same pair the Layouts tab offers: add it as it is, or hand it to
          the AI as a shape to fill. Hidden while picking slides in bulk, where
          the card's job is to be selected rather than acted on, and until the
          content has actually loaded -- "Add with AI" on an empty tree would
          hand the pipeline nothing to bind into.
        */}
        {!isInMultiSelectMode && slideJSON ? (
          <SavedSlideActions
            slide={slide}
            slideJSON={slideJSON as StaticEntityType}
            onAdd={handleClick}
          />
        ) : null}
      </div>
      <div className="tw-flex tw-h-6 tw-w-full tw-min-w-0 tw-items-center tw-gap-2">
        <Checkbox
          checked={isSelected}
          onCheckedChange={() =>
            handleMultiselectSlide(
              {
                id: slide._id,
                slide: slideJSON as StaticEntityType,
                type: "saved",
                savedSlide: slide,
              },
              isSelected ? "remove" : "add",
            )
          }
          className={cn(
            "tw-hidden tw-shrink-0 group-hover/slide:tw-flex",
            (isInMultiSelectMode || isSelected) && "tw-flex",
            isMenuOpen && "tw-flex",
          )}
        >
          <Checkbox.Toggle className="tw-size-4 tw-rounded !tw-border-0 tw-shadow-xs tw-outline tw-outline-1 !tw-outline-offset-0 tw-outline-blue/5 [&_i]:tw-size-3 [&_i]:tw-text-xs/none" />
        </Checkbox>
        <span className="tw-block tw-min-w-0 tw-flex-1 tw-truncate tw-font-sans tw-text-sm tw-font-normal tw-leading-5 tw-text-blue">
          {slide.title}
        </span>
        <SelectionOrderBadge id={slide._id} />
        {!isInMultiSelectMode && (
          <SingleSlideActions
            slide={slide}
            isOpen={isMenuOpen}
            onOpenChange={setIsMenuOpen}
          />
        )}
      </div>
    </div>
  );
});
